import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApplicationById } from "@/hooks/queries/useApplication";
import { applicationService } from "@/services/applicationService";
import CurrentApplicationView from "@/components/PreScreen/CurrentApplicationView";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@/components/Loader";
import { ArrowLeft } from "lucide-react";
import StatusHistoryDialog from "@/components/PreScreen/StatusHistoryDialog";

const ApplicationReviewContainer = () => {
  const { vacancyId, applicationId } = useParams();
  const {
    data: applicationData,
    isLoading,
    isError,
    error,
  } = useApplicationById(applicationId);
  const [showCV, setShowCV] = useState(true);
  const [tagsPerApplication, setTagsPerApplication] = useState({});
  const [scoresPerApplication, setScoresPerApplication] = useState({});
  const [notesPerApplication, setNotesPerApplication] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [currentVacancy, setCurrentVacancy] = useState(null);
  const [selectedPreviousApplication, setSelectedPreviousApplication] =
    useState(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToastStore;
  // Fetch available tags
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const { data: response } = await applicationService.AllTags();
        console.log("All available tags:", response.tags);
        setAvailableTags(response.tags || []);
      } catch (error) {
        console.error("Error fetching tags:", error);
        showToast({
          message: "Failed to load tags",
          type: "error",
        });
      }
    };
    fetchAllTags();
  }, []);

  // Setup application data
  useEffect(() => {
    if (applicationData) {
      console.log("Full application data:", applicationData);
      setCurrentApplication(applicationData.data);
      setCurrentVacancy(applicationData.data.vacancy);

      // Initialize tags
      if (applicationData.data?.application_tags) {
        console.log(
          "Application tags from API:",
          applicationData.data.application_tags,
        );
        const initialTags = applicationData.data.application_tags.map(
          (tag) => tag.name,
        );
        console.log("Initial tags after mapping:", initialTags);
        setTagsPerApplication({
          [applicationData.data.id]: initialTags,
        });
      } else {
        console.log("No application tags found in data");
        // Initialize with empty array if no tags
        setTagsPerApplication({
          [applicationData.data.id]: [],
        });
      }

      // Initialize scores
      if (applicationData.application?.scorecards) {
        const initialScores =
          applicationData.application.scorecards.map((score) => ({
            vacancy_scorecard_id: score.id,
            score: score.score,
          })) || [];

        setScoresPerApplication({
          [applicationData.application.id]: initialScores,
        });
      }
    }
  }, [applicationData]);

  const handleAddTag = async (application_id, tags) => {
    if (!tags.trim()) return;

    const newTagsArray = tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    const existingTags = tagsPerApplication[application_id] || [];
    const updatedTags = [...existingTags, ...newTagsArray];
    console.log("Updated tags:", updatedTags);

    // Update local state
    setTagsPerApplication((prev) => ({
      ...prev,
      [application_id]: updatedTags,
    }));

    // Send to API
    try {
      const mappedTags = {
        applications: [
          {
            application_id: parseInt(application_id),
            tag_names: updatedTags,
          },
        ],
      };

      await applicationService.BulkTagsUpdate(mappedTags);
      showToast({
        message: "Tags updated successfully",
        type: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["application", applicationId],
      });
    } catch (error) {
      console.error("Failed to update tags:", error);
      showToast({
        message: "Failed to update tags",
        type: "error",
      });
      // Rollback local state on error
      setTagsPerApplication((prev) => ({
        ...prev,
        [application_id]: existingTags,
      }));
    }
  };

  const handleRemoveTag = async (indexToRemove) => {
    if (!currentApplication) return;

    const application_id = currentApplication.id;
    const existingTags = tagsPerApplication[application_id] || [];
    const updatedTags = existingTags.filter(
      (_, index) => index !== indexToRemove,
    );

    // Update local state
    setTagsPerApplication((prev) => ({
      ...prev,
      [application_id]: updatedTags,
    }));

    // Send to API
    try {
      const mappedTags = {
        applications: [
          {
            application_id: parseInt(application_id),
            tag_names: updatedTags,
          },
        ],
      };

      await applicationService.BulkTagsUpdate(mappedTags);
      showToast({
        message: "Tags updated successfully",
        type: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["application", applicationId],
      });
    } catch (error) {
      console.error("Failed to update tags:", error);
      showToast({
        message: "Failed to update tags",
        type: "error",
      });
      // Rollback local state on error
      setTagsPerApplication((prev) => ({
        ...prev,
        [application_id]: existingTags,
      }));
    }
  };

  const handleScoreChange = (applicationId, scores, totalScore) => {
    setScoresPerApplication((prev) => ({
      ...prev,
      [applicationId]: scores,
    }));

    // Store scores in the API
    const storeScores = async () => {
      const mappedScores = {
        applications: [
          {
            application_id: parseInt(applicationId),
            scores: scores.map((score) => ({
              vacancy_score_id: score.vacancy_scorecard_id,
              score: score.score,
            })),
          },
        ],
      };

      try {
        await applicationService.BulkScoreCardsUpdate(mappedScores);
        showToast({
          message: "Scores updated successfully",
          type: "success",
        });
        queryClient.invalidateQueries({
          queryKey: ["application", applicationId],
        });
      } catch (error) {
        console.error("Failed to update scores:", error);
        showToast({
          message: "Failed to update scores",
          type: "error",
        });
      }
    };

    storeScores();
  };

  const handleAddNote = (applicationId, note) => {
    console.log("Note type:", typeof note);
    console.log(
      "Note category apptkr_title:",
      note.note_category?.apptkr_title,
    );
    console.log("Current notes state:", notesPerApplication);
    setNotesPerApplication((prev) => {
      const updatedNotes = {
        ...prev,
        [applicationId]: [...(prev[applicationId] || []), note],
      };
      console.log("Updated notes:", updatedNotes);

      // Store notes
      const storeNotes = async () => {
        const mappedNotes = {
          applications: [
            {
              application_id: parseInt(applicationId),
              comments: updatedNotes[applicationId].map((note) => {
                const commentObj = {
                  comment:
                    typeof note === "object" && note.note ? note.note : note,
                };
                if (typeof note === "object" && note.note_id) {
                  commentObj.comment_id = note.note_id;
                }
                return commentObj;
              }),
            },
          ],
        };

        // try {
        //   await applicationService.BulkNotesUpdate(mappedNotes);
        //   showToast({
        //     message: "Note added successfully",
        //     type: "success"
        //   });
        //   queryClient.invalidateQueries({
        //     queryKey: ["application", applicationId],
        //   });
        // } catch (error) {
        //   console.error("Failed to add note:", error);
        //   showToast({
        //     message: "Failed to add note",
        //     type: "error"
        //   });
        // }
      };

      storeNotes();
      return updatedNotes;
    });
  };

  const handleEditNote = (applicationId, note, old_index) => {
    setNotesPerApplication((prev) => {
      const updatedNotes = {
        ...prev,
        [applicationId]: (prev[applicationId] || []).map(
          (existingNote, index) => (index === old_index ? note : existingNote),
        ),
      };

      // Store notes
      const storeNotes = async () => {
        const mappedNotes = {
          applications: [
            {
              application_id: parseInt(applicationId),
              comments: updatedNotes[applicationId].map((note) => {
                const commentObj = {
                  comment:
                    typeof note === "object" && note.note ? note.note : note,
                };
                if (typeof note === "object" && note.note_id) {
                  commentObj.comment_id = note.note_id;
                }
                return commentObj;
              }),
            },
          ],
        };

        // try {
        //   await applicationService.BulkNotesUpdate(mappedNotes);
        //   showToast({
        //     message: "Note updated successfully",
        //     type: "success"
        //   });
        //   queryClient.invalidateQueries({
        //     queryKey: ["application", applicationId],
        //   });
        // } catch (error) {
        //   console.error("Failed to update note:", error);
        //   showToast({
        //     message: "Failed to usdfghjkpdate note",
        //     type: "error"
        //   });
        // }
      };

      storeNotes();
      return updatedNotes;
    });
  };

  const handleApplicationHistoryOpen = (applicant) => {
    setSelectedPreviousApplication(applicant);
  };

  const handleApplicationHistoryClose = () => {
    setSelectedPreviousApplication(null);
  };

  const goBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-muted flex flex-col items-center justify-center">
        <div className="bg-card rounded-xl shadow-xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Error Loading Application
          </h2>
          <p className="text-foreground mb-6">
            {error?.message || "Failed to load application details"}
          </p>
          <button
            onClick={goBack}
            className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!currentApplication) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="bg-card rounded-xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Application Not Found
          </h2>
          <p className="text-foreground mb-6">
            The application you're looking for doesn't exist or has been
            removed.
          </p>
          <button
            onClick={goBack}
            className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* //Todo: remove the back button and bring the Application Deatils on center*/}
      <div className="min-h-screen bg-muted p-4 flex flex-col items-center">
        <div className="w-full max-w-7xl">
          <div className="mb-4 flex justify-center">
            <div className="bg-card px-4 py-2 rounded-full shadow-md">
              <span className="text-foreground">Application Details</span>
            </div>
          </div>

          <div className="relative w-full">
            <CurrentApplicationView
              direction="none"
              currentApplication={currentApplication}
              currentVacancy={currentVacancy}
              tagsPerApplication={tagsPerApplication}
              availableTags={availableTags}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              showCV={showCV}
              onToggleCV={() => setShowCV(!showCV)}
              onScoreChange={handleScoreChange}
              onAddNote={handleAddNote}
              onEditNote={handleEditNote}
              onShowHistory={handleApplicationHistoryOpen}
            />
          </div>
        </div>
      </div>

      <StatusHistoryDialog
        application={selectedPreviousApplication}
        onClose={handleApplicationHistoryClose}
      />
    </>
  );
};

export default ApplicationReviewContainer;
